// src/pages/admin/CoursesManage.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Popconfirm, message, Typography, Modal, Form, Input as AntInput, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, StopOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

const CoursesManage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [form] = Form.useForm();

  // Simulated API call to fetch courses
  const fetchCourses = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const mockData = [
        {
          id: 1,
          name: 'Introduction to JavaScript',
          description: 'Learn JS basics',
          instructor: 'John Doe',
          duration: '4 weeks',
          level: 'Beginner',
          price: 0,
          language: 'English',
          category: 'Programming',
          tags: ['JavaScript', 'Web Development'],
          prerequisites: 'None',
          objectives: 'Understand JS syntax, Build small projects',
          status: 'Active',
          externalLink: 'https://example.com/js-course',
        },
        // Add more mock data as needed
      ].filter(course =>
        course.name.toLowerCase().includes(search.toLowerCase()) ||
        course.description.toLowerCase().includes(search.toLowerCase())
      );

      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paginatedData = mockData.slice(start, end);

      setCourses(paginatedData);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: mockData.length,
      });
    } catch (error) {
      message.error('Failed to fetch courses');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses(pagination.current, pagination.pageSize, searchText);
  }, [pagination.current, pagination.pageSize, searchText]);

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id', sorter: (a, b) => a.id - b.id }, // Added Id column
    { title: 'Name', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Instructor', dataIndex: 'instructor', key: 'instructor' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Draft', value: 'Draft' },
        { text: 'Inactive', value: 'Inactive' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EyeOutlined />} onClick={() => handleView(record)}>View</Button>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          {record.status !== 'Inactive' && (
            <Popconfirm
              title="Are you sure to deactivate this course?"
              onConfirm={() => handleDeactivate(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button icon={<StopOutlined />} danger>Deactivate</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    setCreateModalVisible(true);
  };

  const handleView = (course) => {
    setSelectedCourse(course);
    setViewModalVisible(true);
  };

  const handleEdit = (course) => {
    setSelectedCourse(course);
    form.setFieldsValue(course); // Pre-fill form with course data
    setEditModalVisible(true);
  };

  const handleDeactivate = async (id) => {
    try {
      const updatedCourses = courses.map(course =>
        course.id === id ? { ...course, status: 'Inactive' } : course
      );
      setCourses(updatedCourses);
      message.success('Course deactivated successfully');
      fetchCourses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to deactivate course');
      console.error(error);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchCourses(newPagination.current, newPagination.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleCreateSubmit = async (values) => {
    try {
      const newCourse = {
        id: courses.length + 1,
        ...values,
        tags: values.tags ? values.tags.split(',').map(tag => tag.trim()) : [],
        status: values.status || 'Draft',
      };
      setCourses([...courses, newCourse]);
      message.success('Course created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      fetchCourses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to create course');
      console.error(error);
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      const updatedCourses = courses.map(course =>
        course.id === selectedCourse.id ? { ...course, ...values, tags: values.tags.split(',').map(tag => tag.trim()) } : course
      );
      setCourses(updatedCourses);
      message.success('Course updated successfully');
      setEditModalVisible(false);
      form.resetFields();
      fetchCourses(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to update course');
      console.error(error);
    }
  };

  const courseFormFields = (
    <>
      <Form.Item name="name" label="Course Title" rules={[{ required: true, message: 'Please enter the course title' }]}>
        <AntInput placeholder="e.g., Introduction to JavaScript" />
      </Form.Item>
      <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Please enter a description' }]}>
        <AntInput.TextArea placeholder="e.g., Learn the basics of JavaScript" rows={4} />
      </Form.Item>
      <Form.Item name="instructor" label="Instructor" rules={[{ required: true, message: 'Please enter the instructor name' }]}>
        <AntInput placeholder="e.g., John Doe" />
      </Form.Item>
      <Form.Item name="duration" label="Duration" rules={[{ required: true, message: 'Please enter the duration' }]}>
        <AntInput placeholder="e.g., 4 weeks" />
      </Form.Item>
      <Form.Item name="level" label="Level" rules={[{ required: true, message: 'Please select the level' }]}>
        <Select placeholder="Select level">
          <Option value="Beginner">Beginner</Option>
          <Option value="Intermediate">Intermediate</Option>
          <Option value="Advanced">Advanced</Option>
        </Select>
      </Form.Item>
      <Form.Item name="price" label="Price (in USD)" rules={[{ required: true, message: 'Please enter the price' }]}>
        <AntInput type="number" placeholder="e.g., 0 for free" min={0} />
      </Form.Item>
      <Form.Item name="language" label="Language" rules={[{ required: true, message: 'Please enter the language' }]}>
        <AntInput placeholder="e.g., English" />
      </Form.Item>
      <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Please enter the category' }]}>
        <AntInput placeholder="e.g., Programming" />
      </Form.Item>
      <Form.Item name="tags" label="Tags (comma-separated)" rules={[{ required: true, message: 'Please enter tags' }]}>
        <AntInput placeholder="e.g., JavaScript, Web Development" />
      </Form.Item>
      <Form.Item name="prerequisites" label="Prerequisites">
        <AntInput.TextArea placeholder="e.g., Basic HTML knowledge" rows={2} />
      </Form.Item>
      <Form.Item name="objectives" label="Learning Objectives" rules={[{ required: true, message: 'Please enter learning objectives' }]}>
        <AntInput.TextArea placeholder="e.g., Understand JS syntax, Build small projects" rows={4} />
      </Form.Item>
      <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select the status' }]}>
        <Select placeholder="Select status">
          <Option value="Active">Active</Option>
          <Option value="Draft">Draft</Option>
          <Option value="Inactive">Inactive</Option>
        </Select>
      </Form.Item>
      <Form.Item name="externalLink" label="External Link">
        <AntInput placeholder="e.g., https://example.com/course" />
      </Form.Item>
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
      <Title level={2} style={{ fontSize: '15px' }}>Manage Courses</Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Input.Search
            placeholder="Search courses by name or description"
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            enterButton
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add New Course
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={courses}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} courses`,
        }}
        onChange={handleTableChange}
      />

      {/* View Modal */}
      <Modal
        title="Course Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={<Button onClick={() => setViewModalVisible(false)}>Close</Button>}
        width={600}
      >
        {selectedCourse && (
          <div>
            <p><strong>Id:</strong> {selectedCourse.id}</p> {/* Added Id to View modal */}
            <p><strong>Title:</strong> {selectedCourse.name}</p>
            <p><strong>Description:</strong> {selectedCourse.description}</p>
            <p><strong>Instructor:</strong> {selectedCourse.instructor}</p>
            <p><strong>Duration:</strong> {selectedCourse.duration}</p>
            <p><strong>Level:</strong> {selectedCourse.level}</p>
            <p><strong>Price:</strong> ${selectedCourse.price}</p>
            <p><strong>Language:</strong> {selectedCourse.language}</p>
            <p><strong>Category:</strong> {selectedCourse.category}</p>
            <p><strong>Tags:</strong> {selectedCourse.tags.map((tag, i) => <Tag key={i}>{tag}</Tag>)}</p>
            <p><strong>Prerequisites:</strong> {selectedCourse.prerequisites || 'None'}</p>
            <p><strong>Objectives:</strong> {selectedCourse.objectives}</p>
            <p><strong>Status:</strong> {selectedCourse.status}</p>
            <p><strong>External Link:</strong> {selectedCourse.externalLink ? <a href={selectedCourse.externalLink} target="_blank" rel="noopener noreferrer">{selectedCourse.externalLink}</a> : 'N/A'}</p>
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal
        title="Add New Course"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          {courseFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Create Course</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Course"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
          {courseFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Update Course</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setEditModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CoursesManage;